import type { LapHistoryResponseDto, LapHistoryRowDto } from '../api/client';
import { formatSignedGapMs } from '../utils/timingPresentation';

export interface LapHistoryViewRow extends LapHistoryRowDto {
  globalGapMs: number | null;
  personalGapMs: number | null;
  fastest: boolean;
  slowest: boolean;
  fastestSectors: [boolean, boolean, boolean];
}

export interface LapHistoryViewModel {
  recordRevision: number;
  globalBestMs: number | null;
  personalBestMs: number | null;
  rows: LapHistoryViewRow[];
}

/** Presentation-only marks. Raw server values remain unchanged in each row. */
export function buildLapHistoryViewModel(history: LapHistoryResponseDto): LapHistoryViewModel {
  const times = history.laps.map((lap) => lap.timeMs);
  const fastestTime = times.length ? Math.min(...times) : null;
  const slowestTime = times.length ? Math.max(...times) : null;
  const sectorMinimums: [number | null, number | null, number | null] = [null, null, null];
  for (const lap of history.laps) {
    lap.sectorTimesMs.forEach((value, index) => {
      const current = sectorMinimums[index];
      if (current === null || value < current) sectorMinimums[index] = value;
    });
  }
  return {
    recordRevision: history.recordRevision,
    globalBestMs: history.globalBestMs,
    personalBestMs: history.personalBestMs,
    rows: history.laps.map((lap) => ({
      ...lap,
      globalGapMs: history.globalBestMs === null ? null : lap.timeMs - history.globalBestMs,
      personalGapMs: history.personalBestMs === null ? null : lap.timeMs - history.personalBestMs,
      fastest: fastestTime !== null && lap.timeMs === fastestTime,
      slowest: slowestTime !== null && lap.timeMs === slowestTime,
      fastestSectors: [
        sectorMinimums[0] !== null && lap.sectorTimesMs[0] === sectorMinimums[0],
        sectorMinimums[1] !== null && lap.sectorTimesMs[1] === sectorMinimums[1],
        sectorMinimums[2] !== null && lap.sectorTimesMs[2] === sectorMinimums[2],
      ],
    })),
  };
}

export interface PauseOverlayOptions {
  trackId: number;
  rulesetId: string;
  loadHistory: (signal: AbortSignal) => Promise<LapHistoryResponseDto>;
  onRestartVerifiedRun: () => void;
  onResume: () => void;
  onReturnToMain: () => void;
  onReducedMotionChange: (enabled: boolean) => void;
  reducedMotion: boolean;
}

/** Reusable accessible pause UI; scene lifecycle owns the supplied callbacks. */
export class PauseOverlay {
  readonly element: HTMLDivElement;
  private readonly status: HTMLParagraphElement;
  private readonly content: HTMLDivElement;
  private readonly retryButton: HTMLButtonElement;
  private readonly options: PauseOverlayOptions;
  private abortController: AbortController | null = null;
  private destroyed = false;
  private visible = false;
  private history: LapHistoryResponseDto | null = null;
  private latestGlobalBestMs: number | null = null;
  private latestPersonalBestMs: number | null = null;
  private latestRevision = 0;
  private previouslyFocused: HTMLElement | null = null;
  private readonly initialFocusTarget: HTMLButtonElement;
  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (!this.visible) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.options.onResume();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = this.getFocusableElements();
    if (focusable.length === 0) {
      event.preventDefault();
      this.element.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (!this.element.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  constructor(uiLayer: HTMLElement, options: PauseOverlayOptions) {
    this.options = options;
    this.element = document.createElement('div');
    this.element.className = 'game-pause-overlay';
    this.element.setAttribute('role', 'dialog');
    this.element.setAttribute('aria-modal', 'true');
    this.element.setAttribute('aria-labelledby', 'pause-overlay-title');
    this.element.setAttribute('aria-describedby', 'pause-overlay-hint pause-overlay-status');
    this.element.tabIndex = -1;
    this.element.addEventListener('keydown', this.handleKeyDown);

    const panel = document.createElement('section');
    panel.className = 'game-pause-panel';
    const title = document.createElement('h3');
    title.className = 'game-pause-title';
    title.id = 'pause-overlay-title';
    title.textContent = 'Paused';
    const hint = document.createElement('p');
    hint.className = 'game-pause-hint';
    hint.id = 'pause-overlay-hint';
    hint.textContent = 'Your run is paused. Resume to continue saving verified lap times.';
    const context = document.createElement('p');
    context.className = 'pause-history-context';
    context.textContent = `Track ${options.trackId} · Ruleset ${options.rulesetId}`;
    this.status = document.createElement('p');
    this.status.className = 'pause-history-status';
    this.status.id = 'pause-overlay-status';
    this.status.setAttribute('aria-live', 'polite');
    this.content = document.createElement('div');
    this.content.className = 'pause-history-content';
    this.retryButton = document.createElement('button');
    this.retryButton.className = 'ui-button ui-button--subtle';
    this.retryButton.type = 'button';
    this.retryButton.textContent = 'Retry';
    this.retryButton.hidden = true;
    this.retryButton.addEventListener('click', () => void this.refresh());

    const settings = document.createElement('label');
    settings.className = 'game-pause-setting';
    const reducedMotion = document.createElement('input');
    reducedMotion.type = 'checkbox';
    reducedMotion.checked = options.reducedMotion;
    reducedMotion.addEventListener('change', () =>
      options.onReducedMotionChange(reducedMotion.checked),
    );
    const settingsText = document.createElement('span');
    settingsText.textContent = 'Reduce camera motion';
    settings.append(reducedMotion, settingsText);

    const actions = document.createElement('div');
    actions.className = 'game-pause-actions';
    const restart = this.createButton('Restart verified run', options.onRestartVerifiedRun);
    const resume = this.createButton('Resume', options.onResume);
    this.initialFocusTarget = resume;
    const back = this.createButton('Return to Main Screen', options.onReturnToMain);
    actions.append(resume, restart, back);
    panel.append(
      title,
      hint,
      context,
      this.status,
      this.content,
      this.retryButton,
      settings,
      actions,
    );
    this.element.append(panel);
    uiLayer.append(this.element);
  }

  setVisible(visible: boolean): void {
    if (this.destroyed) return;
    if (visible && !this.visible) {
      const active = document.activeElement;
      this.previouslyFocused = active instanceof HTMLElement ? active : null;
    }
    this.visible = visible;
    this.element.classList.toggle('is-visible', visible);
    if (visible) {
      this.initialFocusTarget.focus();
      if (!this.history) void this.refresh();
    } else {
      this.restoreFocus();
    }
  }

  async refresh(): Promise<void> {
    if (this.destroyed) return;
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.retryButton.hidden = true;
    this.status.setAttribute('aria-live', 'polite');
    this.status.textContent = 'Loading lap history…';
    try {
      const history = await this.options.loadHistory(controller.signal);
      if (this.destroyed || controller.signal.aborted || !this.visible) return;
      this.history =
        this.latestRevision > history.recordRevision
          ? {
              ...history,
              globalBestMs: this.latestGlobalBestMs,
              personalBestMs: this.latestPersonalBestMs,
              recordRevision: this.latestRevision,
            }
          : history;
      this.renderHistory();
    } catch (error) {
      if (this.destroyed || controller.signal.aborted) return;
      this.status.setAttribute('aria-live', 'polite');
      this.status.textContent =
        error instanceof Error ? error.message : 'Unable to load lap history.';
      this.retryButton.hidden = false;
      this.content.replaceChildren();
    }
  }

  updateReferences(
    globalBestMs: number | null,
    personalBestMs: number | null,
    revision: number,
  ): void {
    if (revision < this.latestRevision) return;
    this.latestGlobalBestMs = globalBestMs;
    this.latestPersonalBestMs = personalBestMs;
    this.latestRevision = revision;
    if (!this.history || revision < this.history.recordRevision) return;
    this.history = { ...this.history, globalBestMs, personalBestMs, recordRevision: revision };
    if (this.visible) this.renderHistory();
  }

  invalidate(): void {
    if (this.destroyed) return;
    this.history = null;
    if (this.visible) void this.refresh();
  }

  destroy(): void {
    this.destroyed = true;
    this.abortController?.abort();
    this.abortController = null;
    this.restoreFocus();
    this.element.removeEventListener('keydown', this.handleKeyDown);
    this.element.remove();
  }

  private getFocusableElements(): HTMLElement[] {
    return Array.from(
      this.element.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      ),
    );
  }

  private restoreFocus(): void {
    const target = this.previouslyFocused;
    this.previouslyFocused = null;
    if (target?.isConnected) target.focus();
  }

  private createButton(label: string, handler: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'ui-button';
    button.type = 'button';
    button.textContent = label;
    button.addEventListener('click', handler);
    return button;
  }

  private renderHistory(): void {
    if (!this.history) return;
    const view = buildLapHistoryViewModel(this.history);
    // Reference updates are intentionally quiet; only load/error transitions
    // use aria-live so realtime record events do not interrupt screen readers.
    this.status.setAttribute('aria-live', 'off');
    this.status.textContent = `Global record: ${formatTime(view.globalBestMs)} · Personal best: ${formatTime(view.personalBestMs)} · Revision ${view.recordRevision}`;
    this.retryButton.hidden = true;
    if (view.rows.length === 0) {
      this.content.innerHTML =
        '<p class="pause-history-empty">No completed laps in this race session.</p>';
      return;
    }
    const table = document.createElement('table');
    table.className = 'pause-history-table';
    const caption = document.createElement('caption');
    caption.textContent = 'Laps completed in this race session';
    table.append(caption);
    table.insertAdjacentHTML(
      'beforeend',
      '<thead><tr><th scope="col">Lap</th><th scope="col">Car</th><th scope="col">Time</th><th scope="col">Sectors</th><th scope="col">Record gap</th><th scope="col">PB gap</th></tr></thead>',
    );
    const body = document.createElement('tbody');
    view.rows.forEach((row, index) => {
      const tr = document.createElement('tr');
      const marks = [
        row.fastest ? 'FAST' : '',
        row.slowest ? 'SLOW' : '',
        ...row.fastestSectors.flatMap((best, sector) => (best ? [`S${sector + 1} BEST`] : [])),
        row.visitedPitStop ? 'PIT' : '',
      ].filter(Boolean);
      tr.innerHTML = `<th scope="row">${index + 1}</th><td>${row.carId}</td><td class="tabular-nums">${formatTime(row.timeMs)}${marks.length ? ` <span class="pause-history-marks">${marks.join(' · ')}</span>` : ''}</td><td class="tabular-nums">${row.sectorTimesMs.map((time, sector) => `S${sector + 1} ${formatTime(time)}`).join(' · ')}</td><td class="tabular-nums">${formatGap(row.globalGapMs)}</td><td class="tabular-nums">${formatGap(row.personalGapMs)}</td>`;
      body.append(tr);
    });
    table.append(body);
    this.content.replaceChildren(table);
  }
}

function formatTime(value: number | null): string {
  return value === null ? '—' : (value / 1000).toFixed(3);
}

function formatGap(value: number | null): string {
  return value === null ? '—' : formatSignedGapMs(value);
}
